// Browser-side third-party emote resolution + text tokenization (7TV / BetterTTV /
// FrankerFaceZ). Port of the homelab service's emotes.rs, same layered
// tokenization, so a Twitch overlay renders identical emotes whether it runs in
// the browser (this file) or through the service. These APIs are CORS-open.
//
// The map is layered rather than flat so a live 7TV change can be applied in
// place: the channel's 7TV set is its own layer, and a removed emote falls back
// to whatever a lower layer (BTTV, FFZ, globals) has under the same name.
//
// A channel's lists start from the copies saved last time and are replaced one
// by one as each provider answers, so an overlay draws emotes from its first
// message. Every replacement is announced (onEmotesChanged), and the chat
// clients repair the rows they drew before it (emoteRepair).

import type { MessageSegment } from './overlayMessage';
import { loadSavedList, saveList } from './emoteStore';

export interface EmoteInfo {
  url: string;
  zeroWidth: boolean;
  /** 7TV emote id (7TV rows only), so the renderer can reach the 4x art. */
  id?: string;
  personal?: boolean;
  /** A modifier emote. FrankerFaceZ's attach to the emote before them,
   *  BetterTTV's to the one after (across one space). Attached, a hidden one
   *  draws nothing and a visible one stacks on its emote; alone, it draws as an
   *  ordinary emote, the way both providers draw an orphan. */
  attaches?: 'previous' | 'next';
  hidden?: boolean;
}

/** Name -> emote for one source. */
export type EmoteLayer = Map<string, EmoteInfo>;

type BasePart = 'bttvGlobal' | 'ffzGlobal' | 'stvGlobal' | 'bttvRoom' | 'ffzRoom';

export interface EmoteMap {
  /** Globals, then FFZ/BTTV channel emotes: everything that does not change live. */
  base: EmoteLayer;
  /** The lists `base` is built from, so one that answers late replaces its own
   *  part in place. */
  baseParts: Record<BasePart, EmoteLayer>;
  /** The channel's 7TV set, highest priority, patched by the live feed. */
  seventv: EmoteLayer;
  /** The 7TV set id behind `seventv`, when the channel has one. */
  seventvSetId: string | null;
  /** The channel owner's 7TV user: switching the active set is an update of it. */
  seventvUserId: string | null;
  /** `seventv` is the saved copy (or empty) because 7TV did not answer (yet). */
  seventvStale: boolean;
  /** FrankerFaceZ's subscriber-only effect emotes and the logins that may use them. */
  ffzSub?: { layer: EmoteLayer; logins: Set<string> };
  /** Where the lists are saved and announced: a Twitch room id, or `kick:<id>` /
   *  `google:<id>` (emoteScope). */
  scope?: string;
  /** Whether fresh channel lists are saved as the last good copy; 'spare' only
   *  in a free slot (a Shared Chat partner: never in place of the overlay's own
   *  channels). */
  persist?: boolean | 'spare';
  /** The channel's lists were saved last time, so they are good to draw with
   *  before any provider answers. A cold channel's rows wait for its answers
   *  (EMOTE_HOLD_MS) instead of showing words that become emotes a moment later. */
  warm?: boolean;
}

/** How long a cold channel's first rows wait for its emote lists, at most.
 *  Rows that wait show complete; past this they show and are repaired.
 *  Measured 2026-10-21 against a 3.5 s 7TV answer (a 1,000-emote set): 4 s let
 *  every row show complete, 2 s left 17 of 39 rows changing after they showed. */
export const EMOTE_HOLD_MS = 4_000;

/** A provider's answer: `body` null means it answered that there is nothing
 *  (a 404: no FFZ room, no BTTV or 7TV account). `ok` false means it did not
 *  answer (down, an error status, or too slow), which is when a saved list
 *  stands in. */
export type ProviderAnswer = { ok: true; body: any | null } | { ok: false };

/** A provider that sends nothing for this long has stalled. Slow is fine as
 *  long as bytes keep coming: a big 7TV set is megabytes. */
export const EMOTE_IDLE_TIMEOUT_MS = 10_000;
/** No answer gets longer than this in all. */
export const EMOTE_FETCH_CAP_MS = 120_000;

export async function fetchProvider(url: string, signal?: AbortSignal): Promise<ProviderAnswer> {
  // A plain controller and timers: OBS's browser can be older than
  // AbortSignal.any.
  const ctrl = new AbortController();
  let idle = setTimeout(() => ctrl.abort(), EMOTE_IDLE_TIMEOUT_MS);
  const cap = setTimeout(() => ctrl.abort(), EMOTE_FETCH_CAP_MS);
  const alive = () => {
    clearTimeout(idle);
    idle = setTimeout(() => ctrl.abort(), EMOTE_IDLE_TIMEOUT_MS);
  };
  const onAbort = () => ctrl.abort();
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (res.status === 404) return { ok: true, body: null };
    if (!res.ok) return { ok: false };
    alive();
    const reader = res.body && typeof res.body.getReader === 'function' ? res.body.getReader() : null;
    return { ok: true, body: reader ? await readJson(reader, alive) : await res.json() };
  } catch {
    return { ok: false };
  } finally {
    clearTimeout(idle);
    clearTimeout(cap);
    signal?.removeEventListener('abort', onAbort);
  }
}

/** A streamed body as JSON, telling `alive` about every chunk that arrives. */
async function readJson(reader: ReadableStreamDefaultReader<Uint8Array>, alive: () => void): Promise<any> {
  const decoder = new TextDecoder();
  let text = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    alive();
    text += decoder.decode(value, { stream: true });
  }
  text += decoder.decode();
  return JSON.parse(text);
}

/** Waits between tries of a provider that did not answer: 5 s, doubling, at
 *  most 5 minutes. */
export function retryDelayMs(attempt: number): number {
  return Math.min(5_000 * 2 ** Math.max(0, attempt), 300_000);
}

// ─── Change events ──────────────────────────────────────────────────────────

/** A table changed (`heal`: a list landed or recovered; `gain`: a live edit or
 *  set switch), or some chatters' personal emotes arrived. */
export type EmoteChange =
  | { kind: 'table'; scope: string; mode: 'heal' | 'gain' }
  /** `immediate`: repair before returning (the rows are about to be shown). */
  | { kind: 'personal'; userIds: string[]; immediate?: boolean };

const changeListeners = new Set<(change: EmoteChange) => void>();

/** Hear every emote table change on the page. Returns a disposer. */
export function onEmotesChanged(listener: (change: EmoteChange) => void): () => void {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

export function emitEmotesChanged(change: EmoteChange): void {
  for (const listener of [...changeListeners]) {
    try {
      listener(change);
    } catch {
      /* one listener's failure never stops the others */
    }
  }
}

// ─── Platforms ──────────────────────────────────────────────────────────────

/** 7TV's names for the platforms a channel set can belong to (YouTube is GOOGLE). */
export type SevenTvPlatform = 'TWITCH' | 'KICK' | 'GOOGLE';

/** Where a channel's lists are kept: a Twitch room id as is, others prefixed,
 *  so a Kick id never reads as the Twitch room with the same number. */
export function emoteScope(platform: SevenTvPlatform, id: string): string {
  return platform === 'TWITCH' ? id : `${platform.toLowerCase()}:${id}`;
}

export function seventvUserUrl(platform: SevenTvPlatform, id: string): string {
  const path = platform === 'TWITCH' ? 'twitch' : platform === 'KICK' ? 'kick' : 'google';
  return `https://7tv.io/v3/users/${path}/${encodeURIComponent(id)}`;
}

// ─── Loading ────────────────────────────────────────────────────────────────

// The lists behind `base`, lowest priority first: globals, then the channel's
// own emotes, so a channel emote beats every global of the same name; inside
// each group 7TV beats FFZ beats BetterTTV. That is the 7TV extension's order
// and the app's (`EmoteLookup::build`). 7TV's channel set is its own layer above
// all of these.
const BASE_PARTS = [
  { part: 'bttvGlobal', scope: 'global', source: 'bttv', url: () => 'https://api.betterttv.net/3/cached/emotes/global' },
  { part: 'ffzGlobal', scope: 'global', source: 'ffz', url: () => 'https://api.frankerfacez.com/v1/set/global' },
  { part: 'stvGlobal', scope: 'global', source: '7tv', url: () => 'https://7tv.io/v3/emote-sets/global' },
  { part: 'bttvRoom', scope: 'room', source: 'bttv', url: (room: string) => `https://api.betterttv.net/3/cached/users/twitch/${room}` },
  { part: 'ffzRoom', scope: 'room', source: 'ffz', url: (room: string) => `https://api.frankerfacez.com/v1/room/id/${room}` },
] as const;

type PartSpec = (typeof BASE_PARTS)[number];

function emptyParts(): Record<BasePart, EmoteLayer> {
  return { bttvGlobal: new Map(), ffzGlobal: new Map(), stvGlobal: new Map(), bttvRoom: new Map(), ffzRoom: new Map() };
}

function partLayer(part: BasePart, body: any): { layer: EmoteLayer; sub?: EmoteMap['ffzSub'] } {
  const layer: EmoteLayer = new Map();
  if (!body) return { layer };
  if (part === 'ffzGlobal') return ffzGlobalLayers(body);
  if (part === 'ffzRoom') addFfz(layer, body.sets);
  else if (part === 'bttvGlobal') addBttv(layer, body);
  else if (part === 'stvGlobal') addSeventv(layer, body.emotes);
  else {
    addBttv(layer, body.channelEmotes);
    addBttv(layer, body.sharedEmotes);
  }
  return { layer };
}

/** FFZ's global answer: the sets in `default_sets` are everyone's; any other
 *  set (the subscriber effects) belongs to the logins `users` lists for it. */
function ffzGlobalLayers(body: any): { layer: EmoteLayer; sub?: EmoteMap['ffzSub'] } {
  const layer: EmoteLayer = new Map();
  const sets = body?.sets && typeof body.sets === 'object' ? body.sets : {};
  const defaults: unknown = body?.default_sets;
  if (!Array.isArray(defaults)) {
    addFfz(layer, sets);
    return { layer };
  }
  const isDefault = (id: string) => defaults.some((d) => String(d) === id);
  const everyone: Record<string, unknown> = {};
  const others: Record<string, unknown> = {};
  for (const [id, set] of Object.entries(sets)) (isDefault(id) ? everyone : others)[id] = set;
  addFfz(layer, everyone);
  const subLayer: EmoteLayer = new Map();
  addFfz(subLayer, others);
  const logins = new Set<string>();
  for (const id of Object.keys(others)) {
    const list: unknown = body?.users?.[id];
    if (Array.isArray(list)) for (const l of list) if (typeof l === 'string') logins.add(l.toLowerCase());
  }
  return { layer, sub: subLayer.size > 0 && logins.size > 0 ? { layer: subLayer, logins } : undefined };
}

/** The channel's 7TV answer (`7tv.io/v3/users/<platform>/<id>`) as a layer and
 *  the ids the live feed follows. A null body is a channel with no 7TV. */
export function seventvChannelOf(body: any | null): { layer: EmoteLayer; setId: string | null; userId: string | null } {
  const layer: EmoteLayer = new Map();
  if (!body) return { layer, setId: null, userId: null };
  addSeventv(layer, body.emote_set?.emotes);
  const setId = body.emote_set?.id ?? body.emote_set_id;
  const userId = body.user?.id;
  return { layer, setId: typeof setId === 'string' && setId ? setId : null, userId: typeof userId === 'string' ? userId : null };
}

/** The channel's 7TV list. 7TV is moving the set out of the user answer (only
 *  `emote_set_id` left); then the set is read by id, and a failed read is a
 *  failed answer, never an empty set. */
async function fetchSevenTvChannel(
  platform: SevenTvPlatform,
  id: string,
  signal?: AbortSignal,
): Promise<{ ok: false } | { ok: true; layer: EmoteLayer; setId: string | null; userId: string | null }> {
  const answer = await fetchProvider(seventvUserUrl(platform, id), signal);
  if (!answer.ok) return { ok: false };
  const fresh = seventvChannelOf(answer.body);
  const inline = Array.isArray(answer.body?.emote_set?.emotes);
  if (!fresh.setId || inline) return { ok: true, ...fresh };
  const set = await fetchProvider(`https://7tv.io/v3/emote-sets/${encodeURIComponent(fresh.setId)}`, signal);
  if (!set.ok || !set.body) return { ok: false };
  addSeventv(fresh.layer, set.body.emotes);
  return { ok: true, ...fresh };
}

function rebuildBase(map: EmoteMap) {
  map.base.clear();
  for (const { part } of BASE_PARTS) for (const [k, v] of map.baseParts[part]) map.base.set(k, v);
}

/** Keep the channel's 7TV layer as its last good list. Called on every fresh
 *  answer, live edit and set switch, so the saved copy is never older than what
 *  the overlay last drew. A list that is not kept (a Shared Chat partner's) is
 *  skipped. */
export function saveSeventvChannel(scope: string, map: EmoteMap): void {
  if (map.persist === false) return;
  saveList(map.scope ?? scope, '7tv', map.seventv, { seventvSetId: map.seventvSetId, seventvUserId: map.seventvUserId }, map.persist === 'spare');
}

// Global lists are the same for every channel: one answer serves them all for a
// while. A failed answer is not kept.
const GLOBAL_TTL_MS = 10 * 60_000;
const globalAnswers = new Map<string, { at: number; answer: Promise<ProviderAnswer> }>();

function fetchGlobal(url: string): Promise<ProviderAnswer> {
  const hit = globalAnswers.get(url);
  if (hit && Date.now() - hit.at < GLOBAL_TTL_MS) return hit.answer;
  const answer = fetchProvider(url);
  globalAnswers.set(url, { at: Date.now(), answer });
  void answer.then((a) => {
    if (!a.ok && globalAnswers.get(url)?.answer === answer) globalAnswers.delete(url);
  });
  return answer;
}

export interface EmoteLoad {
  /** Usable at once: the saved lists, replaced in place as answers land. */
  map: EmoteMap;
  /** Settles once every list has answered or failed its first ask. */
  ready: Promise<EmoteMap>;
}

/**
 * A channel's emotes. Twitch channels get BetterTTV, FFZ and 7TV; Kick and
 * YouTube channels get 7TV only (the other two do not support them, and the app
 * does the same). Every list starts from the copy saved last time and is
 * replaced in place by its provider's answer; a list whose provider did not
 * answer is asked again with backoff until it does. The 7TV channel list is
 * retried by the live feed (seventvLive), which also follows a switch of the
 * channel's set; `seventvStale` tells it to. Each replacement is announced as a
 * `heal` of `map.scope`. Aborting `signal` stops the retries.
 */
export function startEmotes(
  platform: SevenTvPlatform,
  id: string,
  opts: { signal?: AbortSignal; persist?: boolean | 'spare' } = {},
): EmoteLoad {
  const { signal } = opts;
  const scope = emoteScope(platform, id);
  const persist = opts.persist ?? true;
  const parts: readonly PartSpec[] = platform === 'TWITCH' ? BASE_PARTS : BASE_PARTS.filter((p) => p.part === 'stvGlobal');
  const baseParts = emptyParts();
  for (const p of parts) baseParts[p.part] = loadSavedList(p.scope === 'global' ? 'global' : scope, p.source)?.layer ?? new Map();
  const saved = loadSavedList(scope, '7tv');
  const map: EmoteMap = {
    base: new Map(),
    baseParts,
    seventv: saved?.layer ?? new Map(),
    seventvSetId: saved?.seventvSetId ?? null,
    seventvUserId: saved?.seventvUserId ?? null,
    seventvStale: true,
    scope,
    persist,
    warm: saved !== null,
  };
  rebuildBase(map);
  const healed = () => emitEmotesChanged({ kind: 'table', scope, mode: 'heal' });

  const land = (p: PartSpec, body: any) => {
    const { layer, sub } = partLayer(p.part, body);
    map.baseParts[p.part] = layer;
    if (p.part === 'ffzGlobal') map.ffzSub = sub;
    rebuildBase(map);
    if (p.scope === 'global') saveList('global', p.source, layer);
    else if (map.persist) saveList(scope, p.source, layer, undefined, map.persist === 'spare');
    healed();
  };

  const urlOf = (p: PartSpec) => (p.url as (room: string) => string)(id);
  const ask = (p: PartSpec) => (p.scope === 'global' ? fetchGlobal(urlOf(p)) : fetchProvider(urlOf(p), signal));

  // A list that did not answer is asked again until it does.
  const retry = (p: PartSpec, attempt: number) => {
    if (signal?.aborted) return;
    const timer = setTimeout(async () => {
      if (signal?.aborted) return;
      const answer = await ask(p);
      if (signal?.aborted) return;
      if (!answer.ok) retry(p, attempt + 1);
      else land(p, answer.body);
    }, retryDelayMs(attempt));
    signal?.addEventListener('abort', () => clearTimeout(timer), { once: true });
  };

  const partsDone = parts.map(async (p) => {
    const answer = await ask(p);
    if (signal?.aborted) return;
    if (answer.ok) land(p, answer.body);
    else retry(p, 0);
  });

  const seventvDone = fetchSevenTvChannel(platform, id, signal).then((fresh) => {
    if (signal?.aborted || !fresh.ok) return;
    // In place: whoever already holds the map keeps the same layer object.
    map.seventv.clear();
    for (const [k, v] of fresh.layer) map.seventv.set(k, v);
    map.seventvSetId = fresh.setId;
    map.seventvUserId = fresh.userId;
    map.seventvStale = false;
    saveSeventvChannel(scope, map);
    healed();
  });

  return { map, ready: Promise.all([...partsDone, seventvDone]).then(() => map) };
}

/** A Twitch channel's emotes, once every list has answered or failed its first
 *  ask (see startEmotes). */
export async function loadEmotes(roomId: string, signal?: AbortSignal): Promise<EmoteMap> {
  return startEmotes('TWITCH', roomId, { signal }).ready;
}

// ─── Provider rows ──────────────────────────────────────────────────────────

/**
 * One 7TV active emote (a set row: the channel's alias at the root, the emote
 * itself under `data`) as an EmoteInfo, or null when it is malformed.
 *
 * Zero-width lives on the EMOTE, as bit 256 of `data.flags`. The row's own
 * `flags` is a different, set-level field where the same property is bit 1, so
 * reading 256 off the row never matches anything.
 */
export function seventvActiveEmote(e: any): { name: string; info: EmoteInfo } | null {
  const name = e?.name;
  const data = e?.data ?? e;
  const id = data?.id ?? e?.id;
  if (typeof name !== 'string' || typeof id !== 'string') return null;
  const emoteFlags = typeof data?.flags === 'number' ? data.flags : 0;
  const rowFlags = typeof e?.flags === 'number' ? e.flags : 0;
  const zeroWidth = (emoteFlags & 256) !== 0 || (rowFlags & 1) !== 0;
  return { name, info: { id, url: `https://cdn.7tv.app/emote/${id}/3x.webp`, zeroWidth } };
}

/** Whether 7TV approved this emote for use in a personal set. A personal set
 *  can hold emotes that are not, and those only work in the owner's channel. */
export function isPersonalUseEmote(e: any): boolean {
  const state = (e?.data ?? e)?.state;
  return Array.isArray(state) && state.includes('PERSONAL');
}

export function addSeventv(map: EmoteLayer, emotes: any): void {
  if (!Array.isArray(emotes)) return;
  for (const e of emotes) {
    const row = seventvActiveEmote(e);
    if (row) map.set(row.name, row.info);
  }
}

/** BetterTTV's modifiers (BetterTTV's own `EMOTE_MODIFIERS`): each attaches to
 *  the emote after it and draws nothing. A `modifier: true` emote not listed
 *  here is an ordinary emote, as BetterTTV draws it. */
const BTTV_MODIFIER_CODES = new Set(['w!', 'h!', 'v!', 'l!', 'r!', 'c!', 'p!', 's!', 'z!']);

/** BetterTTV's legacy overlay emotes, drawn over the emote before them. Its API
 *  does not flag them, so they are known by id (the app and the relay keep the
 *  same list). */
const BTTV_OVERLAY_EMOTE_IDS = new Set([
  '5e76d338d6581c3724c0f0b2', // cvHazmat
  '5e76d399d6581c3724c0f0b8', // cvMask
  '5849c9a4f52be01a7ee5f79d', // IceCold
  '567b5b520e984428652809b6', // SoSnowy
  '58487cc6f52be01a7ee5f205', // SantaHat
  '5849c9c8f52be01a7ee5f79e', // TopHat
  '567b5c080e984428652809ba', // CandyCane
  '567b5dc00e984428652809bd', // ReinDeer
]);

function addBttv(map: EmoteLayer, arr: any): void {
  if (!Array.isArray(arr)) return;
  for (const e of arr) {
    const code = e?.code;
    const id = e?.id;
    if (typeof code !== 'string' || typeof id !== 'string') continue;
    const info: EmoteInfo = { url: `https://cdn.betterttv.net/emote/${id}/3x`, zeroWidth: BTTV_OVERLAY_EMOTE_IDS.has(id) };
    if (e?.modifier === true && BTTV_MODIFIER_CODES.has(code)) {
      info.attaches = 'next';
      info.hidden = true;
    }
    map.set(code, info);
  }
}

/** The largest file an FFZ size map lists (not every emote has a 4 or a 2). */
function largestFfzUrl(urls: any): string | undefined {
  if (!urls || typeof urls !== 'object') return undefined;
  const raw = urls['4'] || urls['2'] || urls['1'];
  return typeof raw === 'string' ? raw : undefined;
}

function addFfz(map: EmoteLayer, sets: any): void {
  if (!sets || typeof sets !== 'object') return;
  for (const set of Object.values<any>(sets)) {
    const emoticons = set?.emoticons;
    if (!Array.isArray(emoticons)) continue;
    for (const e of emoticons) {
      const name = e?.name;
      if (typeof name !== 'string') continue;
      // Animated emotes list their moving files apart from the still ones.
      const raw = largestFfzUrl(e?.animated) ?? largestFfzUrl(e?.urls);
      if (!raw) continue;
      const url = raw.startsWith('//') ? `https:${raw}` : raw.startsWith('http') ? raw : `https://${raw}`;
      const info: EmoteInfo = { url, zeroWidth: false };
      if (e?.modifier === true) {
        // Bit 1 of `modifier_flags` is Hidden: the effect emotes draw nothing
        // of their own. One without it (a channel-made hat) stacks on its emote.
        info.attaches = 'previous';
        info.hidden = ((typeof e.modifier_flags === 'number' ? e.modifier_flags : 0) & 1) !== 0;
      }
      map.set(name, info);
    }
  }
}

/**
 * Apply a live 7TV emote-set change (a ChangeMap) to a layer in place, the way
 * the 7TV extension does, with no refetch: pulled rows out, pushed rows in, and
 * an update (a rename is an update whose name changed) as both.
 * `personalOnly` keeps only emotes approved for personal use.
 */
export function applySeventvChange(layer: EmoteLayer, body: any, personalOnly = false): void {
  const rows = (list: any, field: 'value' | 'old_value') =>
    Array.isArray(list) ? list.filter((c: any) => c?.key === 'emotes').map((c: any) => c?.[field]) : [];
  const remove = (old: any) => {
    const gone = seventvActiveEmote(old);
    if (!gone) return;
    // Name AND id: only the row that was pulled goes, never a newer row that
    // took the same name.
    const cur = layer.get(gone.name);
    if (cur && cur.id === gone.info.id) layer.delete(gone.name);
  };
  for (const old of rows(body?.pulled, 'old_value')) remove(old);
  for (const old of rows(body?.updated, 'old_value')) remove(old);
  for (const v of [...rows(body?.updated, 'value'), ...rows(body?.pushed, 'value')]) {
    if (personalOnly && !isPersonalUseEmote(v)) continue;
    const row = seventvActiveEmote(v);
    if (row) layer.set(row.name, personalOnly ? { ...row.info, personal: true } : row.info);
  }
}

// ─── Tokenization ───────────────────────────────────────────────────────────

/**
 * Replace whole-word emote matches inside each Text segment, leaving already-
 * tokenized (native) emotes untouched. A sender's personal emotes win over the
 * channel's, the same order the 7TV extension and the app use. `login` is the
 * sender's, for FFZ's subscriber effects.
 */
export function applyEmotes(
  segments: MessageSegment[],
  map: EmoteMap,
  personal?: EmoteLayer,
  login?: string,
): MessageSegment[] {
  const sub = login && map.ffzSub?.logins.has(login.toLowerCase()) ? map.ffzSub.layer : undefined;
  if (map.base.size === 0 && map.seventv.size === 0 && !personal?.size && !sub) return segments;
  const lookup = (word: string) => personal?.get(word) ?? map.seventv.get(word) ?? sub?.get(word) ?? map.base.get(word);
  const out: MessageSegment[] = [];
  const modifiers = new Map<MessageSegment, EmoteInfo>();
  // Neighbouring text runs are matched as one text, so the spaces where they
  // meet form one run, the one run the renderer bridges under a zero-width emote.
  let text = '';
  for (const seg of segments) {
    if (seg.type === 'text') {
      text += seg.content;
      continue;
    }
    if (text) tokenizeText(text, lookup, out, modifiers);
    text = '';
    out.push(seg);
  }
  if (text) tokenizeText(text, lookup, out, modifiers);
  return settleModifiers(out, modifiers);
}

// Every space the chatter typed stays in the text, the one before an emote
// too, so "3 KEKW" never draws as "3KEKW". The app's tokenizer keeps them the
// same way, and the renderer bridges one space run under a zero-width emote.
function tokenizeText(
  content: string,
  lookup: (w: string) => EmoteInfo | undefined,
  out: MessageSegment[],
  modifiers: Map<MessageSegment, EmoteInfo>,
): void {
  let buf = '';
  const words = content.split(' ');
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (i > 0) buf += ' ';
    const info = lookup(word);
    if (info) {
      if (buf) {
        out.push({ type: 'text', content: buf });
        buf = '';
      }
      const seg: MessageSegment = {
        type: 'emote',
        content: word,
        emote_url: info.url,
        emote_id: info.id,
        is_zero_width: info.zeroWidth || undefined,
        is_personal: info.personal || undefined,
      };
      if (info.attaches) modifiers.set(seg, info);
      out.push(seg);
    } else {
      buf += word;
    }
  }
  if (buf) out.push({ type: 'text', content: buf });
}

/**
 * Settle modifier emotes against their neighbours: one attached to an emote
 * (right beside it, or across exactly one space) is hidden when it draws
 * nothing, or stacks on it as a zero-width emote; one with no emote beside it
 * keeps its own art. The overlay draws no effects, so an attached hidden
 * modifier just disappears, as it does in the app minus the effect.
 *
 * tokenizeText keeps every typed space, so one space is a ' ' run and two are
 * '  ', the same next to a Twitch emote as next to one it made. A hidden one
 * takes the space it bridged with it: left behind, that space and the next one
 * would be two runs, and a zero-width emote after them would lose its base.
 */
function settleModifiers(segs: MessageSegment[], modifiers: Map<MessageSegment, EmoteInfo>): MessageSegment[] {
  if (modifiers.size === 0) return segs;
  const isEmote = (s?: MessageSegment) => s?.type === 'emote';
  const isGap = (s?: MessageSegment) => s?.type === 'text' && s.content === ' ';
  const out: MessageSegment[] = [];
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    const info = modifiers.get(seg);
    if (!info) {
      out.push(seg);
      continue;
    }
    const previous = info.attaches === 'previous';
    const near = previous ? out[out.length - 1] : segs[i + 1];
    const far = previous ? out[out.length - 2] : segs[i + 2];
    const bridged = isGap(near) && isEmote(far);
    if (!isEmote(near) && !bridged) {
      out.push(seg);
    } else if (!info.hidden && seg.type === 'emote') {
      out.push({ ...seg, is_zero_width: true });
    } else if (bridged) {
      if (previous) out.pop();
      else i++;
    }
  }
  return out;
}
